import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useEffect, useRef } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface RenameVideoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentTitle: string;
  onConfirm: (newTitle: string) => Promise<void>;
}

export function RenameVideoModal({
  open,
  onOpenChange,
  currentTitle,
  onConfirm
}: RenameVideoModalProps) {
  useLanguage();
  const [newTitle, setNewTitle] = useState(currentTitle ?? '');
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('videos', 'edit');

  useEffect(() => {
    if (open && !canEdit) {
      toast.error(translateInterfaceText("Sem permissão para renomear vídeos"));
      onOpenChange(false);
    }
  }, [open, canEdit, onOpenChange]);
  
  useEffect(() => {
    if (open) {
      setNewTitle(currentTitle ?? '');
      // Focus input after modal opens
      setTimeout(() => inputRef.current?.select(), 100);
    }
  }, [open, currentTitle]);
  
  const handleConfirm = async () => {
    const trimmedTitle = newTitle.trim();
    
    if (!trimmedTitle) {
      return;
    }
    
    if (trimmedTitle === currentTitle) {
      onOpenChange(false);
      return;
    }
    
    setIsLoading(true);
    try {
      await onConfirm(trimmedTitle);
      onOpenChange(false);
    } catch (error) {
      // Error is handled by parent
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleConfirm();
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        hideCloseButton={true}
        className="w-[92vw] sm:w-[clamp(420px,42vw,480px)] sm:max-w-none p-0 rounded-[18px] shadow-[0px_8px_32px_rgba(0,0,0,0.12)]"
      >
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="text-xl font-semibold text-foreground">
            {translateInterfaceText("Renomear vídeo")}</DialogTitle>
        </DialogHeader>
        
        <div className="px-6 pb-2 space-y-3">
          <div className="space-y-2.5">
            <Label 
              htmlFor="video-title" 
              className="text-sm font-medium text-foreground"
            >
              {translateInterfaceText("Novo nome")}</Label>
            <Input
              ref={inputRef}
              id="video-title"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={translateInterfaceText("Digite o novo nome do vídeo")}
              maxLength={100}
              disabled={isLoading}
              className="h-11 px-4 text-[15px] border-border focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-all"
            />
            <p className="text-xs font-medium text-muted-foreground pl-0.5">
              {newTitle.length}/100 caracteres
            </p>
          </div>
        </div>
        
        <DialogFooter className="px-6 py-4 border-t border-border bg-muted/30 rounded-b-[18px] gap-3">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
            className="h-10 px-5 font-medium transition-colors"
          >
            {translateInterfaceText("Cancelar")}</Button>
          <Button 
            onClick={handleConfirm}
            disabled={isLoading || !newTitle.trim() || newTitle.trim() === currentTitle}
            className="h-10 px-6 font-medium bg-primary hover:bg-primary/90 shadow-sm transition-all"
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {translateInterfaceText("Renomear")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
